import { z } from 'zod';
import { ExpressionLogic, PRIMITIVE_NAMES_SET } from '@/modules/form/lang';
import { OTHER_OPTION } from './types';
import type { MessageIntent } from './types';
import { BASE64_RE as UUID80_BASE64_RE } from '@/uuid_extensions/uuid80';

const TONE_ID_RE = /^[a-z][a-z0-9-]*$/;

/**
 * The one intent group a templates file may declare. A file's own `bodyWraps`
 * name it, and validation holds the file to it exactly, so a custom override
 * cannot drop an intent the share flow needs. `satisfies` keeps the group
 * honest against the runtime union.
 *
 * There is a single group today. The machinery below is still written over a
 * LIST of groups, because what a file may declare is a question about the file
 * rather than about this one answer to it — and because the last time a second
 * group existed (the news reshare) is recent enough to be worth not
 * re-deriving. A reshare is now the `intro` intent in its reshare sharing mode,
 * which is a per-slot question inside a form rather than an intent of its own.
 */
export const MESSAGE_INTENTS = [
  'intro',
  'reminder',
  'update',
] as const satisfies readonly MessageIntent[];

export const INTENT_GROUPS: readonly (readonly MessageIntent[])[] = [
  MESSAGE_INTENTS,
];

/** Every intent any file may declare, for the `intent` enums below. */
export const ALL_INTENTS = MESSAGE_INTENTS;

/**
 * The group these intents form, or null when they match none of them exactly —
 * a partial group, a mixture, or an empty list all read as no group.
 */
export function intentGroupOf(
  intents: Iterable<string>
): readonly MessageIntent[] | null {
  const declared = new Set(intents);
  return (
    INTENT_GROUPS.find(
      group =>
        group.length === declared.size && group.every(i => declared.has(i))
    ) ?? null
  );
}

const INTENT_GROUP_LIST = INTENT_GROUPS.map(g => g.join(' / ')).join(', or ');

/**
 * A user field id is a base64 UUID80 (`@/uuid_extensions/uuid80`): 14
 * characters of `A–Z a–z 0–9 - _`. The shipped files keep theirs readable by
 * padding a word with underscores (`greeting______`), which a body can still
 * reference as `{greeting______}`.
 */
export function isUserFieldId(id: string): boolean {
  return UUID80_BASE64_RE.test(id);
}

// A primitive name written as a whole option, without the braces that make it
// a reference.
const BARE_NAME_RE = /^_[a-zA-Z][a-zA-Z0-9_]*$/;

/**
 * Validate an options array (either `options` or `optionsSingletonMode`):
 * duplicate detection, at most one OTHER sentinel, and primitive checks.
 * A primitive in an option is always braced, whether it is the whole
 * option (`{_profileName}`) or part of one (`Hey {_recipientInformalName}`),
 * and is validated against `PRIMITIVE_NAMES_SET`.
 */
function validateOptions(
  fieldId: string,
  options: readonly string[],
  ctx: z.RefinementCtx
): void {
  let otherCount = 0;
  const seen = new Set<string>();
  for (const opt of options) {
    if (opt === OTHER_OPTION) {
      otherCount += 1;
      continue;
    }
    if (seen.has(opt)) {
      ctx.addIssue({
        code: 'custom',
        message: `Field "${fieldId}" has duplicate option "${opt}"`,
      });
    }
    if (BARE_NAME_RE.test(opt)) {
      ctx.addIssue({
        code: 'custom',
        message: `Field "${fieldId}" option "${opt}" names a primitive without braces; write it as {${opt}}`,
      });
    }
    // Validate every `{token}` within the option, so a typo'd token cannot
    // silently survive into composed messages.
    const expr = ExpressionLogic.template(opt, 'asWritten');
    for (const token of ExpressionLogic.freeNames(expr)) {
      if (token.startsWith('_') && !PRIMITIVE_NAMES_SET.has(token)) {
        ctx.addIssue({
          code: 'custom',
          message: `Field "${fieldId}" option "${opt}" references unknown primitive {${token}}`,
        });
      }
    }
    for (const issue of ExpressionLogic.issues(expr)) {
      ctx.addIssue({
        code: 'custom',
        message: `Field "${fieldId}" option "${opt}": ${issue}`,
      });
    }
    seen.add(opt);
  }
  if (otherCount > 1) {
    ctx.addIssue({
      code: 'custom',
      message: `Field "${fieldId}" may declare the "${OTHER_OPTION}" sentinel at most once`,
    });
  }
}

const FieldSchema = z
  .object({
    id: z.string().min(1),
    label: z.string().min(1),
    help: z.string().optional(),
    options: z.array(z.string().min(1)).optional(),
    optionsSingletonMode: z.array(z.string().min(1)).optional(),
  })
  .superRefine((field, ctx) => {
    const isUser = isUserFieldId(field.id);

    if (!isUser) {
      ctx.addIssue({
        code: 'custom',
        message: `Field id "${field.id}" must be a base64 UUID80 (14 characters of A–Z a–z 0–9 - _)`,
      });
      return;
    }

    if (field.options) {
      validateOptions(field.id, field.options, ctx);
    }
    if (field.optionsSingletonMode) {
      validateOptions(field.id, field.optionsSingletonMode, ctx);
    }
  });

const BodyWrapSchema = z.object({
  intent: z.enum(ALL_INTENTS),
  prefix: z.string(),
  suffix: z.string(),
});

/**
 * Token rules for a fully-resolved body: every `{token}` must be a primitive
 * name or a declared user field, and every declared user field must be
 * referenced. Run from the file-level refine (where `bodyWraps` is in scope to
 * resolve a `wrappedBody`), so it operates on the effective body either way.
 */
function validateBodyTokens(
  intent: string,
  body: string,
  fields: readonly { id: string }[],
  ctx: z.RefinementCtx,
  path: (string | number)[]
): void {
  const declaredFieldIds = new Set(fields.map(f => f.id));

  const expr = ExpressionLogic.template(body, 'asWritten');
  const tokensInBody = new Set(ExpressionLogic.freeNames(expr));
  for (const issue of ExpressionLogic.issues(expr)) {
    ctx.addIssue({
      code: 'custom',
      path,
      message: `Intent "${intent}" body: ${issue}`,
    });
  }

  tokensInBody.forEach(token => {
    const isPrimitive = PRIMITIVE_NAMES_SET.has(token);
    const isUserDeclared = isUserFieldId(token) && declaredFieldIds.has(token);
    if (!isPrimitive && !isUserDeclared) {
      ctx.addIssue({
        code: 'custom',
        path,
        message: `Intent "${intent}" body references undeclared token {${token}}`,
      });
    }
  });

  for (const field of fields) {
    if (isUserFieldId(field.id) && !tokensInBody.has(field.id)) {
      ctx.addIssue({
        code: 'custom',
        path,
        message: `Intent "${intent}" declares user field "${field.id}" but body does not reference {${field.id}}`,
      });
    }
  }
}

const TemplateSchema = z
  .object({
    intent: z.enum(ALL_INTENTS),
    // A template supplies exactly one of these (a master may author a literal
    // `body`; default files use `wrappedBody`). Token validation runs on the
    // resolved body in the file-level refine. If both are present, `body` wins.
    body: z.string().min(1).optional(),
    wrappedBody: z.string().min(1).optional(),
    fields: z.array(FieldSchema).default([]),
  })
  .superRefine((t, ctx) => {
    if (t.body === undefined && t.wrappedBody === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: `Intent "${t.intent}" must set either "body" or "wrappedBody"`,
      });
    }
  });

// Which intents a tone must carry is a file-level question — the file's
// `bodyWraps` decide it — so a tone only rules out repeating one here.
const TemplateSetSchema = z
  .object({
    id: z
      .string()
      .regex(TONE_ID_RE, 'tone id must be lowercase letters/digits/dashes'),
    name: z.string().min(1),
    icon: z.string().optional(),
    templates: z.array(TemplateSchema).min(1),
  })
  .superRefine((s, ctx) => {
    const seen = new Set<string>();
    for (const t of s.templates) {
      if (seen.has(t.intent)) {
        ctx.addIssue({
          code: 'custom',
          message: `Tone "${s.id}" has duplicate intent "${t.intent}"`,
        });
      }
      seen.add(t.intent);
    }
  });

/**
 * A templates file: `version`, the per-intent `bodyWraps`, and the tones.
 *
 * The file's own `bodyWraps` name the intents it is for, and they must form one
 * whole intent group — the three message intents, or the two news ones. Every
 * tone then carries exactly that group, so which kind of file this is falls out
 * of what it declares rather than out of a separate schema per kind.
 */
export const TemplatesFileSchema = z
  .object({
    version: z.literal(1),
    bodyWraps: z.array(BodyWrapSchema).min(1),
    tones: z.array(TemplateSetSchema).min(1),
  })
  .superRefine((file, ctx) => {
    const seen = new Set<string>();
    for (const tone of file.tones) {
      if (seen.has(tone.id)) {
        ctx.addIssue({
          code: 'custom',
          message: `Duplicate tone id "${tone.id}"`,
        });
      }
      seen.add(tone.id);
    }

    // bodyWraps must cover each of the file's intents exactly once.
    const wrapByIntent = new Map<string, { prefix: string; suffix: string }>();
    file.bodyWraps.forEach((wrap, i) => {
      if (wrapByIntent.has(wrap.intent)) {
        ctx.addIssue({
          code: 'custom',
          path: ['bodyWraps', i],
          message: `Duplicate bodyWraps intent "${wrap.intent}"`,
        });
      }
      wrapByIntent.set(wrap.intent, wrap);
    });

    const fileIntents = intentGroupOf(wrapByIntent.keys());
    if (!fileIntents) {
      ctx.addIssue({
        code: 'custom',
        path: ['bodyWraps'],
        message: `bodyWraps must declare one whole intent group (${INTENT_GROUP_LIST})`,
      });
      return;
    }

    // Every tone carries the file's intents — all of them, and nothing else.
    file.tones.forEach((tone, ti) => {
      const toneIntents = new Set(tone.templates.map(t => t.intent));
      for (const intent of fileIntents) {
        if (!toneIntents.has(intent)) {
          ctx.addIssue({
            code: 'custom',
            path: ['tones', ti],
            message: `Template set "${tone.id}" is missing intent "${intent}"`,
          });
        }
      }
      for (const intent of Array.from(toneIntents)) {
        if (!fileIntents.includes(intent)) {
          ctx.addIssue({
            code: 'custom',
            path: ['tones', ti],
            message: `Tone "${tone.id}" declares intent "${intent}", which this file's bodyWraps do not`,
          });
        }
      }
    });

    // Resolve each template's effective body and validate its tokens.
    file.tones.forEach((tone, ti) => {
      tone.templates.forEach((tmpl, tj) => {
        const path = ['tones', ti, 'templates', tj] as (string | number)[];
        let effectiveBody: string | undefined;
        if (tmpl.body !== undefined) {
          effectiveBody = tmpl.body;
        } else if (tmpl.wrappedBody !== undefined) {
          const wrap = wrapByIntent.get(tmpl.intent);
          if (!wrap) {
            ctx.addIssue({
              code: 'custom',
              path,
              message: `Intent "${tmpl.intent}" uses wrappedBody but bodyWraps has no entry for it`,
            });
          } else {
            effectiveBody = wrap.prefix + tmpl.wrappedBody + wrap.suffix;
          }
        }
        if (effectiveBody !== undefined) {
          validateBodyTokens(tmpl.intent, effectiveBody, tmpl.fields, ctx, [
            ...path,
            'body',
          ]);
        }
      });
    });
  });

export type TemplatesFile = z.infer<typeof TemplatesFileSchema>;
